import React, { useEffect, useState } from 'react';

import { Button, customDateFormatter, intl, intlFmt } from 'ehs-react';
import { useViewStateStore } from '../../stores/viewState.store';
import type { at } from '../../generated/Beans';

type AppointmentDataWrapper = at.sense.portlets.saf.virtualvisitpatient.model.AppointmentDataWrapper;

export function AppointmentSelectionMenu(): JSX.Element {
  const [nextState] = useViewStateStore(store => [store.nextState]);

  return (
    <>
      <div className="vvpp-content">
        <div className="flex-container-title">
          <p className="headline-text">
            {intl('virtualvisitpatientportlet.appointments.title')}
          </p>
        </div>
        <AppointmentList />
      </div>
      <div className="vvpp-footer">
        <div className="vvpp-new-vv-card">
          <div>{intl('virtualvisitpatientportlet.appointments.question')}</div>
          <Button
            id="newVV"
            variant="primary"
            disabled={false}
            onClick={nextState}
            aria-label={intl(
              'virtualvisitpatientportlet.appointments.button.start'
            )}
            text={intl('virtualvisitpatientportlet.appointments.button.start')}
          />
        </div>
      </div>
    </>
  );
}

function AppointmentList(): JSX.Element {
  const [, setNextDisabled] = useState(true);

  const [appointments] = useViewStateStore(state => [
    state.viewStateData?.appointmentList
  ]);

  useEffect(() => {
    setNextDisabled(!appointments || appointments.length != 0);
  }, [appointments]);

  return (
    <>
      {appointments?.map(appointmentWrapper => (
        <AppointmentCard
          key={appointmentWrapper.appointmentId}
          wrapper={appointmentWrapper}
        />
      ))}
    </>
  );
}

function AppointmentCard(prop: {
  wrapper: AppointmentDataWrapper;
}): JSX.Element {
  return (
    <div key={prop.wrapper.appointmentId}>
      <div className="vvpp-appointment-card">
        <AppointmentInfo wrapper={prop.wrapper} />
        <PhysicianInfo wrapper={prop.wrapper} />
        <AvailabilityInfo availability={prop.wrapper.status} />
        <JoinButton wrapper={prop.wrapper} />
      </div>
    </div>
  );
}

function AppointmentInfo(prop: {
  wrapper: AppointmentDataWrapper;
}): JSX.Element {
  return (
    <div className="vvpp-appointment-info">
      <div>{prop.wrapper?.speciality + ' / ' + prop.wrapper?.serviceType}</div>
      <div>
        {customDateFormatter({
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          minute: 'numeric',
          hour: 'numeric',
          hour12: false
        }).formatDate(
          new Date(
            prop.wrapper.startDateTime == null ? '' : prop.wrapper.startDateTime
          )
        ) +
          ' (' +
          prop.wrapper.minutesDuration +
          ' ' +
          intl('view.time.interval.minutes.short') +
          ')'}
      </div>
    </div>
  );
}

function PhysicianInfo(prop: { wrapper: AppointmentDataWrapper }): JSX.Element {
  return (
    <div className="vvpp-appointment-details-physician">
      {prop.wrapper?.physicianName}
    </div>
  );
}

function AvailabilityInfo(prop: {
  availability: at.sense.portlets.saf.virtualvisitpatient.application.AppointmentAvailability;
}): JSX.Element {
  const statusLabel: string =
    intl('virtualvisitpatientportlet.appointments.card.label.status') + ' ';
  let statusText: string;
  switch (prop.availability) {
    case 'AVAILABLE':
      statusText = intl(
        'virtualvisitpatientportlet.appointments.card.availability.available'
      );
      break;
    case 'CANCELED':
      statusText = intl(
        'virtualvisitpatientportlet.appointments.card.availability.canceled'
      );
      break;
    case 'UPCOMING':
      statusText = intlFmt(
        'virtualvisitpatientportlet.appointments.card.availability.future',
        10
      );
      break;
    case 'FROM_PAST':
      statusText = intl(
        'virtualvisitpatientportlet.appointments.card.availability.past'
      );
      break;
    default:
      statusText = '';
  }
  return (
    <div className="vvpp-appointment-availability">
      <div>{statusLabel}</div>
      <div>{statusText}</div>
    </div>
  );
}

function JoinButton(prop: { wrapper: AppointmentDataWrapper }): JSX.Element {
  const canceledOrFromPast: boolean =
    prop.wrapper.status == 'CANCELED' || prop.wrapper.status == 'FROM_PAST';

  const [nextState, selectAppointment] = useViewStateStore(store => [
    store.nextState,
    store.selectAppointment
  ]);

  const join = () => {
    selectAppointment(prop.wrapper);
    nextState();
  };

  return (
    <div
      className={
        canceledOrFromPast ? 'vvpp-join-button-hidden' : 'vvpp-join-button'
      }
    >
      <Button
        id={'join-' + prop.wrapper.appointmentId}
        variant={canceledOrFromPast ? 'gray' : 'primary'}
        disabled={!prop.wrapper.canRegisterNow}
        onClick={join}
        aria-label={intl('view.button.join')}
        text={intl('view.button.join')}
      />
    </div>
  );
}
